import {
  Button,
  ButtonVariant,
  Checkbox,
  Icon,
  IconName,
  useSnackBar,
  Table,
  makeInitialTableState,
} from '@usealloy/component-library';
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Dropdown,
  DropdownToggle,
  Tooltip,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import styled from 'styled-components';
import pluralize from 'pluralize';
import { snakeToCaps } from '~/helpers/formatting';
import {
  useFilteredData, useSortedData, usePaginatedData, useFetchedUserContext, useEvidencePermissions,
} from '~/helpers/hooks';
import { typhonAjaxFetch } from '~/helpers/api';
import { typhonRoutes } from '~/helpers/routes';
import log from '~/helpers/logging';
import {
  DEFAULT_COLUMNS,
  FACETS,
  HIDDEN_FACETS,
  TRX_COLUMNS,
} from '~/entities/evaluations/transaction/history/constants';
import {
  EmptyRow, LoadingRow, DataRow,
} from '~/entities/evaluations/transaction/history/row-states';
import AmountFilter from '~/entities/evaluations/transaction/history/AmountFilter';
import TransactionStatusModal from '~/entities/evaluations/transaction/status/TransactionStatusModal';
import CustomizableDataDropdown from '~/components/CustomizableDataDropdown';
import { TablePagination } from '~/table/TablePagination';
import DropdownFacet from '~/queries/DropdownFacet';
import { DateTimeFilter } from '~/table/filters';
import {
  ComponentWrapper,
  FilterHeaderWrapper,
  FilterWrapper,
  StyledTableDiv,
  TableWrapper,
  ColumnPickerWrapper,
  StyledTableHeader,
  StyledTableHead,
} from '~/entities/evaluations/transaction/history/TransactionHistoryStyles';
import ExportCSV from '~/entities/evaluations/transaction/history/ExportCSV';
import ActionButton from '~/entities/evaluations/transaction/status/ActionButton';
import TransactionMetaModal from '~/entities/evaluations/transaction/history/TransactionMetaModal';
import { Status } from '~/entities/evaluations/transaction/status/status';
import { getFormattedTransactions } from '~/entities/evaluations/transaction/history/utils';
import { formatCurrencyIntl, DEFAULT_CURRENCY_CODE } from '~/helpers/i18n';
import { tooltipZIndexModifiers } from '~/constants/reactstrap';
import { EvidenceModal } from '~/views/EvidenceModal';
import { EVIDENCE_DATA_TYPES } from '~/constants';

const ColumnPickerIcon = styled(Icon)`
  &&&& {
    margin-right .625rem;
  }
`;

const FilterSubHeader = styled.span`
  margin-left: 2.5rem;
`;

const SCROLL_END_PADDING = 20;
const ACTIONABLE_STATUSES = [Status.Paused];

const TransactionHistoryPropTypes = {
  transactions: PropTypes.arrayOf(PropTypes.object),
  transactionColumns: PropTypes.objectOf(PropTypes.array).isRequired,
  entity: PropTypes.object.isRequired,
  agent: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  view: PropTypes.string,
  enableExport: PropTypes.bool,
  enableColumnSelection: PropTypes.bool,
  selectedRows: PropTypes.array,
  setSelectedRows: PropTypes.func,
  enableRowSelection: PropTypes.bool,
  isActionable: PropTypes.bool,
  canSendWebhookFromPage: PropTypes.bool,
  canUpdateStatus: PropTypes.bool,
  caseToken: PropTypes.string,
  hasEvidenceModal: PropTypes.bool,
};
const TransactionHistoryDefaultProps = {
  view: null,
  agent: null,
  enableExport: true,
  enableColumnSelection: true,
  enableRowSelection: false,
  selectedRows: [],
  setSelectedRows: null,
  isActionable: false,
  canSendWebhookFromPage: false,
  canUpdateStatus: false,
  caseToken: null,
  hasEvidenceModal: false,
};

const TransactionHistory = ({
  transactions: transactionsProps,
  transactionColumns,
  loading: entityLoading,
  agent,
  entity,
  view,
  enableExport,
  enableColumnSelection,
  enableRowSelection,
  selectedRows,
  setSelectedRows,
  isActionable: trxTableActionable,
  canSendWebhookFromPage,
  canUpdateStatus,
  caseToken,
  hasEvidenceModal,
}) => {
  const {
    typhonDomain,
  } = useFetchedUserContext();

  // Check evidence permissions (includes feature flag check)
  const { canReadEvidence } = useEvidencePermissions();

  const { external_entity_identifier: extEntityId } = entity;
  const csvTitle = `${extEntityId ? `${extEntityId}_` : ''}transactions_${moment().format('YYYY_MM_DD')}.csv`;

  const generateColumnOptions = columns => !!columns && columns.map(column => ({
    name: snakeToCaps(column),
    prop: column,
    orderBy: false,
  }));
  const isActionable = trx => canSendWebhookFromPage && ACTIONABLE_STATUSES.includes(trx.status);
  // NOTE: the table is operating under the assumption that all transactions are for a single currency.
  //        this does NOT affect the row states, but does affect filtering options. This is a
  //        temporary, naive approach we plan on addressing soon.
  const primaryCurrencyCode = entity?.primary_currency_code
    || DEFAULT_CURRENCY_CODE;

  const {
    secondary_columns: secondaryColumns,
    priority_columns: priorityColumns,
  } = transactionColumns;
  let allTableColumns = [];
  if (priorityColumns && secondaryColumns) {
    allTableColumns = [...priorityColumns, ...secondaryColumns];
  }

  // Filter out evidence_count column if user doesn't have evidence permissions
  if (!canReadEvidence) {
    allTableColumns = allTableColumns.filter(col => col !== 'evidence_count');
  }

  const hiddenColumns = new Set([...DEFAULT_COLUMNS.hiddenFromTable]);

  const [visibleColumns, setVisibleColumns] = useState(generateColumnOptions(allTableColumns));
  const [facets, setFacets] = useState(FACETS);
  const [openFacetKey, setOpenFacetKey] = useState(null);
  const [hiddenFacets, setHiddenFacets] = useState(HIDDEN_FACETS);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [minAmount, setMinAmount] = useState(null);
  const [maxAmount, setMaxAmount] = useState(null);
  const [showColGradient, setShowColGradient] = useState(true);
  const [isClearDateInput, setIsClearDateInput] = useState(false);
  const [displayTrxStatusModal, toggleTrxStatusModal] = useState(false);
  const [displayMetaModal, toggleMetaModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [modalMetadata, setModalMetadata] = useState({});
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [tableState, setTableState] = useState({
    ...makeInitialTableState({
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
    }),
  });

  /*
  Temporary state used to hold internally fetched transactions and differentiate
  them from those provided via props.

  This is necessary because both will be present until we fully move to internally
  fetched transactions for all implementations of the TransactionHistory component.
  */
  const [transactions, setTransactions] = useState(transactionsProps || []);
  const [transactionsStateLoading, setTransactionsStateLoading] = useState(false);

  const addSnackBar = useSnackBar();

  const getTransactionsState = useCallback(async () => {
    const { entity_token: entityToken } = entity;
    const url = `${typhonDomain}${typhonRoutes.entities.transactions.getAll(entityToken)}`;

    try {
      setTransactionsStateLoading(true);
      const transactionsResponse = await typhonAjaxFetch(url);
      const formattedTransactions = getFormattedTransactions(transactionsResponse?.transactions);
      setTransactions(formattedTransactions);
    } catch (error) {
      addSnackBar({
        duration: 2_000,
        id: 'sb-fetch-entity-transactions-failure',
        title: `Failed to fetch transactions for entity token ${entityToken}`,
        type: 'danger',
      });
      log('error', `GET ${url} exception`, {}, error);
    }
    setTransactionsStateLoading(false);
  }, [entity, typhonDomain, addSnackBar]);

  useEffect(() => {
    if (!transactionsProps && entity?.entity_token && typhonDomain) {
      getTransactionsState();
    }
  }, [entity, typhonDomain, getTransactionsState, transactionsProps]);

  const addFlowColumn = trxs => trxs.map(trx => ({
    ...trx,
    flow_amount: trx.amount,
  }));

  const handleMinInputChange = event => {
    const integerValue = Number(event.target.value.replace(/[^0-9]/g, ''));
    const minAmountData = formatCurrencyIntl(integerValue, {
      alphabeticCurrencyCode: primaryCurrencyCode,
    });
    setMinAmount(minAmountData);
  };
  const handleMaxInputChange = event => {
    const integerValue = Number(event.target.value.replace(/[^0-9]/g, ''));
    const maxAmountData = formatCurrencyIntl(integerValue, {
      alphabeticCurrencyCode: primaryCurrencyCode,
    });
    setMaxAmount(maxAmountData);
  };

  const toggleFilterMenu = () => setIsFilterMenuOpen(prevState => !prevState);
  const {
    items: filteredTransactions,
    filterBy,
    updateFilterByClosure,
    convertToLegacyParams,
    clearAllFilters,
  } = useFilteredData({ items: addFlowColumn(transactions), facets });
  const legacyParams = convertToLegacyParams(filterBy);
  const DEFAULT_SORT = [['created_at', 'DESC']];
  const {
    items: sortedItems,
    triggerSort,
    getSortedClassName,
  } = useSortedData(filteredTransactions, DEFAULT_SORT);
  const {
    currentPage, pageItems, pageSize, totalPages, updatePage,
  } = usePaginatedData(sortedItems);
  const lastPage = totalPages - 1;

  const generateFilterOptions = useCallback(() => {
    const getUniqueValues = key => [...new Set(transactions.map(transaction => transaction[key]))];
    setHiddenFacets(incompleteFacets => {
      const completeFacets = incompleteFacets.map(facet => {
        if (facet.key === 'amount') {
          return facet;
        }
        const options = getUniqueValues(facet.key).map(uniqueValue => ({
          key: uniqueValue,
          pk: uniqueValue,
          value: uniqueValue,
          label: uniqueValue,
          booleanFilter(value) {
            return value === uniqueValue;
          },
        })).filter(({ value }) => value && value.length > 0);
        return { ...facet, options };
      });

      return completeFacets;
    });
  }, [transactions]);

  useEffect(() => {
    generateFilterOptions();
  }, [transactions, generateFilterOptions]);

  const handleSelectAllRows = checked => {
    if (checked) {
      setSelectedRows(transactions);
    } else {
      setSelectedRows([]);
    }
  };

  const revealFacets = key => {
    const facetToReveal = hiddenFacets.find(facet => facet.key === key);
    const remainedHidden = hiddenFacets.filter(facet => facet.key !== key);
    setHiddenFacets(remainedHidden);

    if (facetToReveal.key === 'amount') {
      const smallBound = formatCurrencyIntl(10000, {
        alphabeticCurrencyCode: primaryCurrencyCode,
      });
      const mediumBound = formatCurrencyIntl(50000, {
        alphabeticCurrencyCode: primaryCurrencyCode,
      });
      const largeBound = formatCurrencyIntl(100000, {
        alphabeticCurrencyCode: primaryCurrencyCode,
      });

      // NOTE: while it looks odd to take the absolute value, this is very intentional!
      //    In finance, it is fairly standard to operate based on the absolute value of
      //    the amount. Since we have a separate filter for flow (i.e. positive or negative)
      //    we can do this.
      const newOptions = [
        {
          value: 'small',
          label: `Under ${smallBound.formattedNumber}`,
          booleanFilter(value) {
            return Math.abs(value) < smallBound.originalNumber;
          },
        },
        {
          value: 'medium',
          label: `${smallBound.formattedNumber}-${mediumBound.formattedNumber}`,
          booleanFilter(value) {
            return Math.abs(value) >= smallBound.originalNumber
              && Math.abs(value) < mediumBound.originalNumber;
          },
        },
        {
          value: 'large',
          label: `${mediumBound.formattedNumber}-${largeBound.formattedNumber}`,
          booleanFilter(value) {
            return Math.abs(value) >= mediumBound.originalNumber
              && Math.abs(value) < largeBound.originalNumber;
          },
        },
        {
          value: 'extralarge',
          label: `Over ${largeBound.formattedNumber}`,
          booleanFilter(value) {
            return Math.abs(value) >= largeBound.originalNumber;
          },
        },
        {
          // NOTE: This entire filter option is a placeholder, and overwritten later.
          // "hidden" is used as a way to prevent the invalid state of the option
          // from being rendered.
          value: 'custom',
          label: 'Custom Range',
          hidden: true,
          // The booleanFilter() function & label field are later overwritten (and dependent on user input)
        },
      ];
      facetToReveal.options = newOptions;
    }
    setFacets(prevState => [...prevState, facetToReveal]);
  };

  const handleFacetSelect = ({ target: { dataset: { key } } }) => {
    setOpenFacetKey(key);
    revealFacets(key);
  };

  const clearTrxTableFilters = () => {
    clearAllFilters();
    setFacets(FACETS);
    setHiddenFacets(HIDDEN_FACETS);
    generateFilterOptions();
    setIsClearDateInput(true);
  };

  const handleAmountRangeSubmit = ({ minBound, maxBound }) => {
    if (minBound && maxBound) {
      const incompleteAmountFacet = facets.find(facet => facet.key === 'amount');
      const updatedAmountFacet = {
        ...incompleteAmountFacet,
        options: incompleteAmountFacet.options.map(option => {
          if (option.value === 'custom') {
            return {
              ...option,
              label: `${minBound.formattedNumber} to ${maxBound.formattedNumber}`,
              booleanFilter: value => {
                const absValue = Math.abs(value);
                return (absValue >= minBound.originalNumber)
                  && (absValue <= maxBound.originalNumber);
              },
            };
          }
          return option;
        }),
      };
      const updatedFacets = facets.map(facet => {
        if (facet.key === 'amount') return updatedAmountFacet;
        return facet;
      });
      setFacets(updatedFacets);
      const updateFilterBy = updateFilterByClosure('function', updatePage);
      updateFilterBy('amount', 'custom');
    }
  };

  const transactionsTable = useRef(false);

  // set visible table columns
  const loadTrxColumns = useCallback(() => {
    const selectedColumnsByDefault = priorityColumns
      && priorityColumns.filter(column => ![
        'Transaction ID',
        'account_number',
      ].includes(column));

    if (agent) {
      const { email } = agent;
      // User context can come from spa or non-spa app
      const customerId = agent.customerId || agent.customer_id;
      if (email && customerId) {
        const localColumnConfigArr = JSON.parse(
          window.localStorage.getItem('savedTrxColumns'),
        ) || [];
        const agentColumnConfig = localColumnConfigArr.find(
          config => config.customerId === customerId && config.agent === email,
        );
        if (agentColumnConfig) {
          const columns = canReadEvidence
            ? [...agentColumnConfig.selectedTrxColumns]
            : [...agentColumnConfig.selectedTrxColumns].filter(col => col.prop !== 'evidence_count');
          setVisibleColumns(columns);
        } else {
          const defaultPriorityColumns = generateColumnOptions(selectedColumnsByDefault);
          if (defaultPriorityColumns) setVisibleColumns(defaultPriorityColumns);
        }
      }
    } else {
      const defaultPriorityColumns = generateColumnOptions(selectedColumnsByDefault);
      if (defaultPriorityColumns) setVisibleColumns(defaultPriorityColumns);
    }
  }, [agent, priorityColumns, canReadEvidence]);

  useEffect(() => {
    loadTrxColumns();
  }, [transactionColumns, loadTrxColumns]);

  const updateLocalColumnConfig = selectedColumns => {
    const localColumnConfigArr = JSON.parse(window.localStorage.getItem('savedTrxColumns')) || [];
    const { email } = agent;
    const customerId = agent.customerId || agent.customer_id;

    if (customerId && email) {
      const newColumnConfig = {
        customerId,
        agent: email,
        selectedTrxColumns: selectedColumns,
      };

      const oldColumnConfig = localColumnConfigArr.find(
        config => config.customerId === customerId && config.agent === email,
      );

      let updatedTrxColumns = [];
      if (localColumnConfigArr.length && !!oldColumnConfig) {
        updatedTrxColumns = localColumnConfigArr.map(
          columnConfig => {
            if (columnConfig.customerId === customerId && columnConfig.agent === email) {
              return newColumnConfig;
            }
            return columnConfig;
          },
        );
      } else if (localColumnConfigArr.length) {
        updatedTrxColumns = [...localColumnConfigArr, newColumnConfig];
      } else {
        updatedTrxColumns.push(newColumnConfig);
      }

      window.localStorage.setItem('savedTrxColumns', JSON.stringify(updatedTrxColumns));
    }
  };

  const handleUpdateColumns = selectedColumns => {
    updateLocalColumnConfig([...selectedColumns]);
    setVisibleColumns([...selectedColumns]);
  };

  const handleTableScroll = e => {
    const {
      target: {
        scrollLeft: scrollPosition,
        scrollWidth: maxScroll,
        offsetWidth,
      },
    } = e;

    if (maxScroll - scrollPosition - offsetWidth < SCROLL_END_PADDING) {
      setShowColGradient(false);
    } else {
      setShowColGradient(true);
    }
  };
  const renderPageItems = () => (
    pageItems.length
      ? (
        <>
          {pageItems.map(transaction => (
            <DataRow
              key={`${transaction.transaction_id}`}
              trx={transaction}
              isActionable={trxTableActionable}
              tableColumns={visibleColumns
                .filter(({ prop }) => !hiddenColumns.has(prop))
                .filter(({ prop }) => canReadEvidence || prop !== 'evidence_count')
                .map(({ prop }) => prop)}
              isSelectable={enableRowSelection}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              onEvidenceClick={hasEvidenceModal && canReadEvidence ? trx => {
                setSelectedTransactions([trx]);
                setShowEvidenceModal(true);
              } : undefined}
              actionComponent={(
                <ActionButton
                  isActionable={isActionable(transaction)}
                  changeStatus={() => {
                    setSelectedTransactions([transaction]);
                    toggleTrxStatusModal(!displayTrxStatusModal);
                  }}
                  openMetaModal={() => {
                    setModalMetadata(transaction.metadata);
                    toggleMetaModal(true);
                  }}
                  openEvidenceModal={() => {
                    setSelectedTransactions([transaction]);
                    setShowEvidenceModal(true);
                  }}
                  canUpdateStatus={canUpdateStatus}
                  hasMetadata={transaction.metadata && Object.keys(transaction.metadata).length > 0}
                />
              )}
            />
          ))}
          {sortedItems.length > pageSize && lastPage === currentPage && []}
        </>
      )
      : <EmptyRow colSpan={allTableColumns.length || 10} />
  );

  return (
    <>
      <TransactionStatusModal
        isOpen={displayTrxStatusModal}
        closeModal={() => {
          toggleTrxStatusModal(false);
          setModalMetadata({});
        }}
        transactions={selectedTransactions}
        caseToken={caseToken}
      />
      <TransactionMetaModal
        isOpen={displayMetaModal}
        closeModal={() => toggleMetaModal(false)}
        transactionMeta={modalMetadata}
      />
      {canReadEvidence && hasEvidenceModal && (
      <EvidenceModal
        isOpen={showEvidenceModal}
        closeModal={() => {
          setShowEvidenceModal(false);
        }}
        sourceTokens={selectedTransactions
          .map(trx => trx.external_transaction_identifier)
          .filter(Boolean)
          .map(String)}
        sourceType={EVIDENCE_DATA_TYPES.TRANSACTION}
        contextToken={entity?.entity_token}
        sourceComponent={(
          <Table
            columns={TRX_COLUMNS.filter(
              col => canReadEvidence || col.id !== 'evidence_count',
            )}
            data={selectedTransactions}
            onTableStateChange={newState => {
              setTableState(newState);
            }}
            state={tableState}
          />
        )}
      />
      )}
      <ComponentWrapper
        className="content-wrapper table-wrapper"
        ref={transactionsTable}
      >
        <FilterHeaderWrapper className="header-8">
          <div className="col flex start h-space-16">
            <h4>Filter By</h4>
            <Button
              className="btn-link"
              onClick={() => clearTrxTableFilters()}
              variant={ButtonVariant.tertiary}
            >
              Clear Filters
            </Button>
          </div>
        </FilterHeaderWrapper>
        <div className="header-24">
          <div className="col flex space">
            <FilterWrapper className="flex start align-end h-space-8 v-space-8">
              {facets.map(facet => (
                facet.filter_type === 'timestamp' ? (
                  <div key={facet.key}>
                    <span className="small-text">{facet.label}</span>
                    <DateTimeFilter
                      selectedRange={filterBy[facet.key] ? filterBy[facet.key][2] : []}
                      filter={{ prop: facet.key }}
                      updateFilterBy={updateFilterByClosure('between', updatePage)}
                      isClearDateInput={isClearDateInput}
                      setIsClearDateInput={setIsClearDateInput}
                    />
                  </div>
                ) : (
                  <DropdownFacet
                    key={facet.key}
                    isOpen={facet.key === openFacetKey}
                    facet={facet}
                    filterBy={legacyParams.filterBy}
                    updateFilterBy={updateFilterByClosure(facet.rule_type, updatePage)}
                    boundariesElement={transactionsTable.current}
                    additionalComponent={(facet.key === 'amount')
                      ? () => (
                        <AmountFilter
                          handleSubmit={handleAmountRangeSubmit}
                          minBound={minAmount}
                          maxBound={maxAmount}
                          currencyCode={primaryCurrencyCode}
                          handleMinInputChange={handleMinInputChange}
                          handleMaxInputChange={handleMaxInputChange}
                        />
                      ) : null}
                  />
                )
              ))}
              <Dropdown isOpen={isFilterMenuOpen} toggle={toggleFilterMenu} className="facet">
                <DropdownToggle id="add-filter-button" className="secondary nocase no-chevron">
                  <Icon icon={IconName.Add} />
                </DropdownToggle>

                <Tooltip
                  target="add-filter-button"
                  placement="bottom"
                  delay={0}
                  modifiers={tooltipZIndexModifiers}
                >
                  Add Filter
                </Tooltip>

                <DropdownMenu
                  className="small-text"
                  modifiers={{ preventOverflow: transactionsTable.current }}
                >
                  {hiddenFacets.map(({ key, label }) => (
                    <DropdownItem
                      key={key}
                      className="flex start h-space-4 no-wrap no-toggle"
                      data-key={key}
                      onClick={handleFacetSelect}
                    >
                      {label}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </FilterWrapper>
            {enableExport && (
              <ExportCSV
                title={csvTitle}
                columns={visibleColumns.filter(
                  ({ prop }) => canReadEvidence || prop !== 'evidence_count',
                )}
                transactions={filteredTransactions}
              />
            )}
          </div>
        </div>

        <FilterSubHeader className="light-grey">
          {`Showing ${pageItems.length} of the last ${pluralize('transaction', sortedItems.length, true)}`}
        </FilterSubHeader>

        <StyledTableDiv className="table-overflow">
          <TableWrapper onScroll={e => handleTableScroll(e)}>
            <table className="table header-16">
              <StyledTableHeader>
                {enableColumnSelection && (
                  <ColumnPickerWrapper
                    showGradient={showColGradient}
                    view={view}
                  >
                    {!enableRowSelection && (
                      <CustomizableDataDropdown
                        toggleDisplay={trxTableActionable ? (
                          <ColumnPickerIcon icon={IconName.Columns} />
                        ) : (
                          <Icon icon={IconName.Columns} />
                        )}
                        selectedOptions={visibleColumns.filter(
                          ({ prop }) => canReadEvidence || prop !== 'evidence_count',
                        )}
                        handleSelectOptions={
                          selectedOptions => handleUpdateColumns(selectedOptions)
                        }
                        availableOptions={generateColumnOptions(allTableColumns)}
                      />
                    )}
                  </ColumnPickerWrapper>
                )}
                <tr className="row-space-left row-space-right-medium">
                  {enableRowSelection && (
                    <th>
                      <Checkbox
                        defaultChecked={transactions.length === selectedRows.length}
                        onClick={e => handleSelectAllRows(e.target.checked)}
                      />
                    </th>
                  )}
                  {visibleColumns
                    .filter(({ prop }) => !hiddenColumns.has(prop))
                    .filter(({ prop }) => canReadEvidence || prop !== 'evidence_count')
                    .map(({ name, prop }) => (
                      <StyledTableHead
                        className={`no-wrap ${getSortedClassName(name)}`}
                        onClick={() => triggerSort(prop)}
                        key={name}
                      >
                        {snakeToCaps(name)}
                      </StyledTableHead>
                    ))}
                  {trxTableActionable && (
                  <StyledTableHead />
                  )}
                </tr>
              </StyledTableHeader>

              <tbody>
                {entityLoading || transactionsStateLoading
                  ? <LoadingRow colSpan={allTableColumns.length} />
                  : renderPageItems()}
              </tbody>
            </table>
          </TableWrapper>
        </StyledTableDiv>
        {transactions.length > pageSize && (
        <TablePagination
          page={currentPage}
          pages={totalPages}
          updatePage={updatePage}
        />
        )}
      </ComponentWrapper>
    </>
  );
};

TransactionHistory.propTypes = TransactionHistoryPropTypes;
TransactionHistory.defaultProps = TransactionHistoryDefaultProps;

export default TransactionHistory;
